import { useCallback, useEffect, useState } from "react";
import { getPublicPharmacies, type Pharmacie } from "../../api/publicBenin";
import { sendOrdonnanceToPharmacie } from "../../api/professional";
import type { OrdonnanceSendOption } from "./SendOrdonnanceToPharmacyDialog";

type PharmacyOrdonnanceInlineProps = {
  ordonnanceOptions: OrdonnanceSendOption[];
  onSent?: () => void;
};

export default function PharmacyOrdonnanceInline({ ordonnanceOptions, onSent }: PharmacyOrdonnanceInlineProps) {
  const [pharmacies, setPharmacies] = useState<Pharmacie[]>([]);
  const [loadingPharmacies, setLoadingPharmacies] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedOrdonnanceId, setSelectedOrdonnanceId] = useState("");
  const [sendingPharmacieId, setSendingPharmacieId] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  useEffect(() => {
    setSelectedOrdonnanceId(ordonnanceOptions[0]?.id ?? "");
  }, [ordonnanceOptions]);

  useEffect(() => {
    setLoadError(null);
    setLoadingPharmacies(true);
    void getPublicPharmacies({ limit: 100 })
      .then((res) => {
        if (!res.ok) {
          setLoadError(res.message);
          setPharmacies([]);
          return;
        }
        setPharmacies(res.data);
      })
      .finally(() => setLoadingPharmacies(false));
  }, []);

  const handleSendTo = useCallback(
    async (pharmacieId: string) => {
      const ordonnanceId = selectedOrdonnanceId.trim();
      if (!ordonnanceId) {
        setSendError("Sélectionnez une ordonnance.");
        return;
      }
      setSendError(null);
      setSendingPharmacieId(pharmacieId);
      const res = await sendOrdonnanceToPharmacie(ordonnanceId, pharmacieId);
      setSendingPharmacieId(null);
      if (!res.ok) {
        setSendError(res.message);
        return;
      }
      onSent?.();
    },
    [onSent, selectedOrdonnanceId],
  );

  if (ordonnanceOptions.length === 0) {
    return (
      <div className="mt-5 rounded-[8px] border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        Aucune ordonnance avec identifiant API pour cette consultation (consultation ancienne sans migration, ou erreur
        réseau). Réessayez ou vérifiez la fiche patient.
      </div>
    );
  }

  return (
    <div className="mt-5 space-y-4">
      <h3 className="text-sm font-semibold text-[#0B1F16] uppercase tracking-wide">Pharmacies</h3>
      <p className="text-sm text-[#0B1F16]/70">
        Choisissez une pharmacie, puis cliquez sur « Envoyer l&apos;ordonnance » pour lui transmettre la prescription par
        e-mail.
      </p>

      {ordonnanceOptions.length > 1 && (
        <div>
          <label htmlFor="inline-pharma-ordo" className="block text-xs font-medium text-[#0B1F16]/80 mb-1.5">
            Ordonnance à envoyer
          </label>
          <select
            id="inline-pharma-ordo"
            value={selectedOrdonnanceId}
            onChange={(e) => setSelectedOrdonnanceId(e.target.value)}
            disabled={!!sendingPharmacieId}
            className="w-full max-w-xl h-10 rounded-[6px] px-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40 text-sm"
            style={{ borderColor: "rgba(0,0,0,0.15)" }}
          >
            {ordonnanceOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {loadingPharmacies && <p className="text-sm text-[#0B1F16]/60">Chargement des pharmacies…</p>}
      {loadError && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-[8px] px-3 py-2">{loadError}</div>
      )}
      {!loadingPharmacies && !loadError && pharmacies.length === 0 && (
        <p className="text-sm text-[#0B1F16]/60">Aucune pharmacie référencée pour le moment.</p>
      )}

      {!loadingPharmacies && pharmacies.length > 0 && (
        <ul className="space-y-2">
          {pharmacies.map((p) => (
            <li
              key={p.id}
              className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between rounded-[8px] border px-3 py-3 bg-white/80"
              style={{ borderColor: "rgba(0,0,0,0.1)" }}
            >
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[#0B1F16] truncate">{p.nomPharmacie}</div>
                <div className="text-xs text-[#0B1F16]/65">
                  {[p.commune, p.departement].filter(Boolean).join(" · ") || "—"}
                  {p.tel ? ` · ${p.tel}` : ""}
                </div>
              </div>
              <button
                type="button"
                disabled={!!sendingPharmacieId || !selectedOrdonnanceId}
                onClick={() => void handleSendTo(p.id)}
                className="shrink-0 h-9 px-4 rounded-[6px] text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: "#00553E" }}
              >
                {sendingPharmacieId === p.id ? "Envoi…" : "Envoyer l\u2019ordonnance"}
              </button>
            </li>
          ))}
        </ul>
      )}

      {sendError && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-[8px] px-3 py-2">{sendError}</div>
      )}
    </div>
  );
}
